import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';

// config
import { NAV } from '../../../config-global';
// utils
import { bgBlur } from '../../../utils/cssStyles';
//
import Iconify from '../../iconify';
import Scrollbar from '../../scrollbar';
//
import { defaultSettings } from '../config-setting';
import { useSettingsContext } from '../SettingsContext';
import BadgeDot from './BadgeDot';
import Block from './Block';
import ColorPresetsOptions from './ColorPresetsOptions';
import ContrastOptions from './ContrastOptions';
import DirectionOptions from './DirectionOptions';
import FullScreenOptions from './FullScreenOptions';
import LayoutOptions from './LayoutOptions';
import ModeOptions from './ModeOptions';
import StretchOptions from './StretchOptions';
import ToggleButton from './ToggleButton';

// ----------------------------------------------------------------------

const SPACING = 2.5;

const SettingsDrawer = () => {
  const {
    onResetSetting,
    themeColorPresets,
    themeContrast,
    themeDirection,
    themeLayout,
    themeMode,
    themeStretch,
    visible,
  } = useSettingsContext();

  const theme = useTheme();

  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const notDefault =
    themeMode !== defaultSettings.themeMode ||
    themeLayout !== defaultSettings.themeLayout ||
    themeStretch !== defaultSettings.themeStretch ||
    themeContrast !== defaultSettings.themeContrast ||
    themeDirection !== defaultSettings.themeDirection ||
    themeColorPresets !== defaultSettings.themeColorPresets;

  if (!visible) return null;

  return (
    <>
      {!open && (
        <ToggleButton
          notDefault={notDefault}
          onToggle={handleToggle}
          open={open}
        />
      )}

      <Drawer
        anchor="right"
        BackdropProps={{ invisible: true }}
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            ...bgBlur({
              color: theme.palette.background.default,
              opacity: 0.9,
            }),
            boxShadow: `-24px 12px 40px 0 ${alpha(
              theme.palette.mode === 'light'
                ? theme.palette.grey[500]
                : theme.palette.common.black,
              0.16
            )}`,
            width: NAV.W_BASE,
          },
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ pl: SPACING, pr: 1, py: 2 }}
        >
          <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
            Settings
          </Typography>

          <Tooltip title="Reset">
            <Box sx={{ position: 'relative' }}>
              {notDefault && <BadgeDot />}
              <IconButton onClick={onResetSetting}>
                <Iconify icon="ic:round-refresh" />
              </IconButton>
            </Box>
          </Tooltip>

          <IconButton onClick={handleClose}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ p: SPACING, pb: 0 }}>
          <Block title="Mode">
            <ModeOptions />
          </Block>

          <Block title="Contrast">
            <ContrastOptions />
          </Block>

          <Block title="Direction">
            <DirectionOptions />
          </Block>

          <Block title="Layout">
            <LayoutOptions />
          </Block>

          <Block
            title="Stretch"
            tooltip="Only available at large resolutions > 1600px (xl)"
          >
            <StretchOptions />
          </Block>

          <Block title="Presets">
            <ColorPresetsOptions />
          </Block>
        </Scrollbar>

        <Box sx={{ p: SPACING, pt: 0 }}>
          <FullScreenOptions />
        </Box>
      </Drawer>
    </>
  );
};

export default SettingsDrawer;
